import { RecommendedQuestion, HomeRecommendedQuestionsResponse } from '../schemas/chat';

interface CachedData {
  questions: RecommendedQuestion[];
  timestamp: number;
  userId: string | null;
}

const CACHE_KEY = 'recommended_questions_cache';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

/**
 * Get cached recommended questions for the current user
 */
export const getCachedQuestions = (userId: string | null): RecommendedQuestion[] | null => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const data: CachedData = JSON.parse(cached);

    // Check if cache is expired
    const now = Date.now();
    if (now - data.timestamp > CACHE_DURATION) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    // Check if cache is for the same user
    if (data.userId !== userId) {
      return null;
    }

    return data.questions;
  } catch (error) {
    console.error('Error reading cached questions:', error);
    return null;
  }
};

/**
 * Set cached recommended questions for the current user
 */
export const setCachedQuestions = (
  questions: RecommendedQuestion[],
  userId: string | null
): void => {
  try {
    const data: CachedData = {
      questions,
      timestamp: Date.now(),
      userId,
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error caching questions:', error);
  }
};

/**
 * Clear cached recommended questions
 */
export const clearCachedQuestions = (): void => {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (error) {
    console.error('Error clearing cached questions:', error);
  }
};

/**
 * Fetch recommended questions with caching
 */
export const fetchRecommendedQuestions = async (
  fetchFn: () => Promise<HomeRecommendedQuestionsResponse>,
  userId: string | null
): Promise<RecommendedQuestion[]> => {
  // Try to get from cache first
  const cached = getCachedQuestions(userId);
  if (cached && cached.length > 0) {
    return cached;
  }

  // Fetch from API
  const response = await fetchFn();

  // Cache the results
  setCachedQuestions(response.questions, userId);

  return response.questions;
};
